<template>
  <ion-header class="app-header" :class="{ scrolled: isScrolled }">

    <!-- ── MAIN HEADER ── -->
    <ion-toolbar class="header-toolbar">

      <!-- Logo -->
      <div class="logo" slot="start" @click="$router.push('/home')">
        <img
          class="logo-img"
          :src="logoImage"
          alt="Logo"
        />
      </div>

      <!-- Desktop Nav -->
      <nav class="desktop-nav" slot="end">
        <a
          v-for="link in links"
          :key="link.path"
          class="nav-link"
          :class="{ active: isActiveLink(link) }"
          @click="go(link)"
        >
          {{ link.label }}
          <span class="nav-underline"></span>
        </a>

        <!-- Dashboard (role-based) -->
        <button v-if="canShowDashboard" class="nav-dashboard-btn" @click="goDashboard">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
          </svg>
          Dashboard
        </button>

        <!-- Auth CTA -->
        <button v-if="!isLoggedIn" class="nav-login-btn" @click="goLogin">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/></svg>
          Login
        </button>
        <button v-else class="nav-login-btn" @click="onLogout">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M10 12H6m0 0V8m0 4v4m8-8h4m0 0V8m0 4v4"/></svg>
          Logout
        </button>

      </nav>

      <!-- Mobile Hamburger -->
      <button
        class="hamburger"
        slot="end"
        @click="menuOpen = !menuOpen"
        :class="{ open: menuOpen }"
        aria-label="Toggle menu"
      >
        <span></span><span></span><span></span>
      </button>

    </ion-toolbar>

    <!-- ── MOBILE DRAWER ── -->
    <transition name="drawer">
      <div class="mobile-drawer" v-if="menuOpen">
        <a
          v-for="link in links"
          :key="link.path"
          class="drawer-link"
          :class="{ active: isActiveLink(link) }"
          @click="go(link)"
        >
          <span>{{ link.label }}</span>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M9 18l6-6-6-6"/></svg>
        </a>
        <a
          v-if="canShowDashboard"
          class="drawer-link"
          :class="{ active: $route.path.startsWith('/admin/panel') || $route.path.startsWith('/staff/panel') }"
          @click="goDashboard"
        >
          <span>Dashboard</span>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M9 18l6-6-6-6"/></svg>
        </a>
        <div class="drawer-bottom">
          <button v-if="!isLoggedIn" class="drawer-login-btn" @click="goLogin">Login →</button>
          <button v-else class="drawer-login-btn" @click="onLogout">Logout →</button>
        </div>
      </div>
    </transition>

  </ion-header>
</template>

<script>
import { IonHeader, IonToolbar } from '@ionic/vue'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'AppHeader',
  components: { IonHeader, IonToolbar },

  data() {
    return {
      menuOpen:   false,
      isScrolled: false,
      logoPath:   '/riderfield.png',
      links: [
        { label: 'Home',    path: '/home', sectionId: '' },
        { label: 'Bikes',   path: '/home', sectionId: 'bikes' },
        { label: 'Contact', path: '/home', sectionId: 'contact' },
      ],
    }
  },

  computed: {
    ...mapGetters('auth', ['isLoggedIn', 'getRole']),
    logoImage() { return this.logoPath },

    canShowDashboard() {
      return this.isLoggedIn && (this.getRole === 'admin' || this.getRole === 'staff')
    },

    dashboardPath() {
      if (this.getRole === 'admin') return '/admin/panel'
      if (this.getRole === 'staff') return '/staff/panel'
      return '/dashboard'
    },
  },

  mounted() {
    const content = document.querySelector('ion-content')
    if (content) {
      content.addEventListener('ionScroll', (e) => {
        this.isScrolled = e.detail.scrollTop > 10
      })
    }
  },

  beforeUnmount() {
  },

  methods: {
    ...mapActions('auth', ['logout']),

    isActiveLink(link) {
      return this.$route.path === '/home' && (this.$route.hash || '') === (link.sectionId ? `#${link.sectionId}` : '')
    },

    go(link) {
      this.menuOpen = false
      const target = { path: '/home', hash: link.sectionId ? `#${link.sectionId}` : '' }
      const sameTarget = this.$route.path === target.path && (this.$route.hash || '') === (target.hash || '')
      if (sameTarget) {
        this.scrollToSection(link.sectionId)
        return
      }
      this.$router.push(target).then(() => {
        this.$nextTick(() => this.scrollToSection(link.sectionId))
      })
    },

    scrollToSection(sectionId) {
      if (!sectionId) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }
      const el = document.getElementById(sectionId)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },

    goDashboard() {
      this.menuOpen = false
      this.$router.push(this.dashboardPath)
    },

    goLogin() {
      this.menuOpen = false
      this.$router.push('/login')
    },

    async onLogout() {
      await this.logout()
      await this.$nextTick()
      this.menuOpen = false
      this.$router.push('/login')
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Outfit:wght@300;400;500;600;700;800&display=swap');

* { box-sizing: border-box; margin: 0; padding: 0; }

/* ══ MAIN HEADER ══ */
.app-header {
  --background: transparent;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-toolbar {
  --background: #F7F4EF;
  --border-color: transparent;
  --padding-start: 48px;
  --padding-end: 48px;
  --min-height: 72px;
  --max-height: 72px;           /* lock toolbar to exactly 72px */
  height: 72px;
  overflow: hidden;             /* prevent any child from pushing height */
  border-bottom: 1px solid rgba(26, 18, 9, 0.08);
  transition: background 0.35s, box-shadow 0.35s;
  position: relative;
  backdrop-filter: saturate(115%) blur(6px);
}

.app-header.scrolled .header-toolbar {
  --background: rgba(247, 244, 239, 0.97);
  box-shadow:
    0 2px 32px rgba(26, 18, 9, 0.08),
    0 1px 0 rgba(200, 149, 58, 0.2);
}
.app-header.scrolled .header-toolbar::after {
  content: "";
  position: absolute;
  left: 0; right: 0; bottom: -1px;
  height: 2px;
  background: linear-gradient(90deg, #E63946, #C8953A);
  opacity: 0.6;
}

/* ══ LOGO ══ */
.logo {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  text-decoration: none;
  height: 100%;              /* fill toolbar height */
}

.logo-img {
  height: 52px;              /* fits comfortably inside 72px toolbar */
  width: auto;
  max-width: 200px;
  display: block;
  object-fit: contain;
  object-position: left center;
  background: transparent;
  filter: none;
  transform-origin: left center;
  transition: transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1), filter 220ms ease;
  will-change: transform;
}

.logo:hover .logo-img,
.logo:focus-visible .logo-img {
  filter: drop-shadow(0 10px 18px rgba(26, 18, 9, 0.12));
  animation: logo-pop 420ms cubic-bezier(0.2, 0.9, 0.2, 1) both;
}

@keyframes logo-pop {
  0%   { transform: translateY(0) scale(1); }
  45%  { transform: translateY(-1.5px) scale(1.04); }
  100% { transform: translateY(-0.5px) scale(1.02); }
}

@media (prefers-reduced-motion: reduce) {
  .logo-img { transition: none; }
  .logo:hover .logo-img,
  .logo:focus-visible .logo-img { animation: none; transform: none; filter: none; }
}

/* ══ DESKTOP NAV ══ */
.desktop-nav {
  display: flex;
  align-items: center;
  gap: 4px;
}

.nav-link {
  position: relative;
  font-family: 'Outfit', sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgba(26, 18, 9, 0.5);
  padding: 10px 14px;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.22s;
  white-space: nowrap;
  border-radius: 12px;
}
.nav-link:hover  { color: #1A1209; background: rgba(26, 18, 9, 0.06); }
.nav-link.active { color: #E63946; }

.nav-underline {
  position: absolute;
  bottom: 4px; left: 12px; right: 12px;
  height: 2.5px;
  background: linear-gradient(90deg, #E63946, #C8953A);
  border-radius: 999px;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.32s cubic-bezier(0.4, 0, 0.2, 1);
}
.nav-link:hover .nav-underline,
.nav-link.active .nav-underline { transform: scaleX(1); }

/* ── Buttons ── */
.nav-login-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 10px;
  padding: 10px 18px;
  background: transparent;
  color: #E63946;
  font-family: 'Outfit', sans-serif;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  border: 1.5px solid #E63946;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.22s, transform 0.15s, box-shadow 0.22s, color 0.22s;
  white-space: nowrap;
}
.nav-login-btn svg { fill: #E63946; flex-shrink: 0; }
.nav-login-btn:hover { background: rgba(230, 57, 70, 0.06); transform: translateY(-1px); }
.nav-login-btn:active { transform: scale(0.97); }

.nav-dashboard-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 10px;
  padding: 10px 18px;
  background: transparent;
  color: #1A1209;
  font-family: 'Outfit', sans-serif;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  border: 1.5px solid rgba(200, 149, 58, 0.9);
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.22s, transform 0.15s, box-shadow 0.22s, color 0.22s, border-color 0.22s;
  white-space: nowrap;
}
.nav-dashboard-btn svg { fill: #C8953A; flex-shrink: 0; }
.nav-dashboard-btn:hover { background: rgba(200, 149, 58, 0.08); border-color: #C8953A; transform: translateY(-1px); }
.nav-dashboard-btn:active { transform: scale(0.97); }

/* ══ HAMBURGER ══ */
.hamburger {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 42px; height: 42px;
  background: transparent;
  border: 1.5px solid rgba(26, 18, 9, 0.12);
  border-radius: 12px;
  cursor: pointer;
  padding: 10px;
  transition: border-color 0.2s, background 0.2s;
}
.hamburger:hover       { border-color: #E63946; background: rgba(230, 57, 70, 0.05); }
.hamburger.open        { border-color: #E63946; }
.hamburger span {
  display: block;
  width: 20px; height: 1.5px;
  background: #1A1209;
  border-radius: 2px;
  transition: transform 0.32s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s, width 0.25s;
  transform-origin: center;
}
.hamburger.open span:nth-child(1) { transform: translateY(6.5px) rotate(45deg); }
.hamburger.open span:nth-child(2) { opacity: 0; width: 0; }
.hamburger.open span:nth-child(3) { transform: translateY(-6.5px) rotate(-45deg); }

/* ══ MOBILE DRAWER ══ */
.mobile-drawer {
  background: #F7F4EF;
  border-top: 1px solid rgba(26, 18, 9, 0.07);
  padding: 0 0 24px;
  overflow: hidden;
  box-shadow: 0 16px 40px rgba(26, 18, 9, 0.08);
}
.drawer-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: 'Outfit', sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: rgba(26, 18, 9, 0.5);
  padding: 16px 24px;
  border-bottom: 1px solid rgba(26, 18, 9, 0.05);
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s, background 0.2s, padding-left 0.2s;
}
.drawer-link svg            { color: rgba(26, 18, 9, 0.2); transition: color 0.2s, transform 0.2s; }
.drawer-link:hover          { color: #1A1209; background: rgba(230, 57, 70, 0.04); padding-left: 32px; }
.drawer-link:hover svg      { color: #E63946; transform: translateX(3px); }
.drawer-link.active         { color: #E63946; }
.drawer-link.active svg     { color: #E63946; }

.drawer-bottom {
  display: flex;
  gap: 10px;
  padding: 20px 24px 0;
}
.drawer-locate-btn,
.drawer-login-btn {
  flex: 1;
  padding: 14px 16px;
  border-radius: 999px;
  font-family: 'Outfit', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
}
.drawer-locate-btn       { background: transparent; border: 1.5px solid #E63946; color: #E63946; }
.drawer-locate-btn:hover { background: rgba(230, 57, 70, 0.07); transform: scale(1.02); }
.drawer-locate-btn svg   { fill: #E63946; }
.drawer-login-btn        { background: #E63946; border: none; color: #fff; box-shadow: 0 4px 20px rgba(230, 57, 70, 0.3); }
.drawer-login-btn:hover  { background: #B52D38; transform: scale(1.02); box-shadow: 0 6px 24px rgba(230, 57, 70, 0.4); }

/* ══ DRAWER ANIMATION ══ */
.drawer-enter-active { transition: max-height 0.42s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s; overflow: hidden; max-height: 520px; }
.drawer-leave-active { transition: max-height 0.32s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s; overflow: hidden; }
.drawer-enter-from   { max-height: 0; opacity: 0; }
.drawer-leave-to     { max-height: 0; opacity: 0; }

/* ══ RESPONSIVE ══ */

@media (min-width: 1280px) {
  .logo-img { height: 81px; }
}

@media (max-width: 1024px) {
  .header-toolbar { --padding-start: 28px; --padding-end: 28px; }
  .logo-img { height: 76px; }
}

@media (max-width: 768px) {
  .desktop-nav { display: none; }
  .hamburger   { display: flex; }
  .header-toolbar {
    --padding-start: 16px;
    --padding-end: 16px;
    --min-height: 64px;
    --max-height: 64px;
    height: 64px;
  }
  .logo-img {
    height: 63px;        /* big logo, still fits in 64px bar */
    max-width: 160px;
  }
}

@media (max-width: 480px) {
  .header-toolbar {
    --min-height: 60px;
    --max-height: 60px;
    height: 60px;
  }
  .logo-img {
    height: 62px;
    max-width: 150px;
  }
}

@media (max-width: 360px) {
  .header-toolbar {
    --min-height: 56px;
    --max-height: 56px;
    height: 56px;
  }
  .logo-img {
    height: 60px;
    max-width: 130px;
  }
}
</style>